import { buildColors } from '@t4s/ui/tokens'
import { useId } from 'react'
import { Notice } from '../shell/notice.tsx'
import { brandColourState, COLOUR_BADGE, cssColour } from './brand-colour.ts'

/**
 * "What staff will see" — docs/ux/PLATFORM-ADMIN.md §13.2 and §13.3.
 *
 * **Every colour in this card comes from `buildColors`**, called once per scheme with the colour
 * being previewed, and from nothing else: not a role of this stylesheet, and not a literal. That is
 * §5.2's one carve-out (the card is a picture of the *tenant's* app, not of this console) and it is
 * also what makes the contrast shown the contrast the tenant gets. The two exceptions are the
 * as-supplied swatch, which is the owner's own text (drawn only when `buildColors` read it as a
 * colour, so it is always a hex value and never arbitrary text), and the dashed empty swatch for a
 * value it could not read, which is drawn in the console's own border role because it depicts
 * *nothing*.
 *
 * `brand` is the colour being previewed — the typed text, the stored colour, or the last saved one,
 * whichever the screen decided (`branding-screen.tsx`, §13.4). The card does not know which;
 * `typed` only chooses the words of the fallback sentence.
 */
export function ColourPreview({
  brand,
  typed,
}: {
  readonly brand: string
  /** The value is what the owner typed and has not saved, not what is stored. */
  readonly typed: boolean
}) {
  const headingId = useId()
  const light = buildColors('light', brand)
  const dark = buildColors('dark', brand)
  const { state } = brandColourState(brand)
  const fallback = state === 'fallback'
  const badge = COLOUR_BADGE[state]

  // The sentence below is only drawn when the light scheme moved the fill (`brandColourState`), so
  // the only question left is whether the dark scheme moved it as well.
  const where = dark.brand.corrected ? 'both schemes' : 'the light scheme'

  return (
    <section className="card columns-branding-preview" aria-labelledby={headingId}>
      <div className="card-head">
        <div>
          <h2 id={headingId}>What staff will see</h2>
          <p className="muted">
            From the same colour arithmetic the tenant app uses — not a picture of this console.
          </p>
        </div>
        <span className="badge-note">
          <span className={badge.className}>{badge.label}</span>
          {/* The same line the Overview's Branding card prints, so "As supplied" never reads alone. */}
          <span className="tiny muted">{badge.meaning}</span>
        </span>
      </div>
      <div className="card-body">
        {fallback ? (
          <Notice tone="failure">
            <code className="wrap">{brand}</code> could not be read as a colour, so the platform’s
            default brand colour is shown here instead
            {typed
              ? ' — and it is what the tenant’s apps will draw if you save it.'
              : '. This is almost certainly a typo in the stored value.'}
          </Notice>
        ) : state === 'corrected' ? (
          // A warning again (#319): `corrected` is now true only when the fill itself moved, so this
          // is said to the tenants it is true of and not to all of them.
          <Notice tone="warning">
            This colour was adjusted so that text drawn on it stays legible, in {where}. Hue and
            saturation are unchanged, only lightness moved. The value stored for this tenant is
            unchanged; the correction is computed at render time, everywhere, including in the
            preview here.
          </Notice>
        ) : null}

        <ul className="swatches" aria-label="The colour in each scheme">
          <li>
            {fallback ? (
              <span className="swatch-lg swatch-empty" aria-hidden="true" />
            ) : (
              <span
                className="swatch-lg"
                aria-hidden="true"
                style={{ backgroundColor: cssColour(brand) }}
              />
            )}
            <span className="small strong">As supplied</span>
            <code className="tiny muted wrap">{fallback ? 'not readable as a colour' : brand}</code>
          </li>
          <SchemeSwatch name="Light scheme" colors={light} />
          <SchemeSwatch name="Dark scheme" colors={dark} />
        </ul>

        <div className="branding-preview">
          <SchemePreview scheme="light" colors={light} />
          <SchemePreview scheme="dark" colors={dark} />
        </div>
      </div>
    </section>
  )
}

/** One scheme's fill, and a word for what happened to the colour in it. */
function SchemeSwatch({
  name,
  colors,
}: {
  readonly name: string
  readonly colors: ReturnType<typeof buildColors>
}) {
  const fill = colors.brand.solid
  const what = colors.brand.fallback
    ? 'platform default'
    : colors.brand.corrected
      ? 'corrected'
      : 'as supplied'

  return (
    <li>
      <span
        className="swatch-lg"
        aria-hidden="true"
        // The tenant's colour as this scheme draws it, from `buildColors`.
        style={{ backgroundColor: cssColour(fill) }}
      />
      <span className="small strong">{name}</span>
      <code className="tiny muted wrap">
        {fill} · {what}
      </code>
    </li>
  )
}

function SchemePreview({
  scheme,
  colors,
}: {
  readonly scheme: 'light' | 'dark'
  readonly colors: ReturnType<typeof buildColors>
}) {
  return (
    <div
      className="branding-preview-pane"
      style={{ backgroundColor: colors.canvas, color: colors.text.primary }}
    >
      <p className="field-label" style={{ color: colors.text.secondary }}>
        {scheme === 'light' ? 'Light' : 'Dark'}
      </p>
      <button
        type="button"
        className="branding-preview-button"
        style={{ backgroundColor: colors.brand.solid, color: colors.brand.onSolid }}
        disabled
      >
        Primary action
      </button>
      <p style={{ color: colors.brand.text }}>Brand-coloured text, on this scheme’s surface.</p>
      <div
        className="branding-preview-field"
        style={{ borderColor: colors.brand.border, backgroundColor: colors.surface }}
      >
        A bordered field
      </div>
      <p className="small" style={{ color: colors.text.muted }}>
        {/* The same three words as the swatch beside it: an unreadable value draws the platform
            default, so the pane must not claim the supplied colour was used (P07 QA D3). */}
        {colors.brand.fallback
          ? 'Platform default shown in this scheme.'
          : colors.brand.corrected
            ? 'Corrected in this scheme.'
            : 'Used as supplied in this scheme.'}
      </p>
    </div>
  )
}
